import {ReactNode} from 'react'
import cn from 'clsx'
import {Space} from 'antd'
import useStore from 'base/store'
import {IconComponent, PermissionType} from 'common/types'
import {maybePluralize} from 'common/helpers'
import dayjs from 'dayjs';
import styles from './OrgCard.module.scss'

import { 
  Block, Text, 
  Button, Modal, 
  UserAvatar,
  RequestIcon, FolderBookmarkIcon,
  // ArrowIcon, 
  AttachmentLinkLargeIcon
} from 'common/components'
import {useOpen} from 'common/hooks'
import OrgDetailsForm from './OrgDetailsForm'

interface CardProps {
  children?: ReactNode
  data: any
  isRequestLoading?: any
  imgHeight: number
}

export default function OrgCard({
  data,
  isRequestLoading,
}: CardProps) {
  const isVCTeam = (data.type === 'team')
  // console.log(data)
  return (
    isVCTeam ?
    <Block type="grey" className={cn(styles.root)}>
      <div className={styles.block}>
        <div className={styles.panel}>
          <DisplayID displayId={data.displayId} />
          <EditingAction
            initValues={data}
            isRequestLoading={isRequestLoading}
          />
        </div>
        <div className={styles.header}>
          <UserAvatar src={data.photo} size={96} alt={'organisation'} />
          <Text as="h3" size="3xl" weight="medium" mt={16} className={styles['header-text']} >
            {data.name}
          </Text>
        </div>

        <DetailsContent t0={""}
          t1={data.address || '[address ???]'}
          t2={data.email || '[email ???]'}
          t3={data.phone || '[phone ???]'}
        />

      </div>

    </Block>
    :
    <Block type="grey" className={cn(styles.root)}>
      <div className={styles.block}>
        <div className={styles.panel}>
          <DisplayID displayId={data.displayId} />
          <EditingAction
            initValues={data}
            isRequestLoading={isRequestLoading}
          />
        </div>
        <div className={styles.header}>
          <UserAvatar src={data.photo} size={96} alt={'organisation'} />
          <Text as="h3" size="3xl" weight="medium" mt={16} className={styles['header-text']} >
            {data.name}
          </Text>
          <Text color="grey" className={styles['header-text']} mt={24}>
            {`${data.type} - ${data.entityType}`}
          </Text>
        </div>
      </div>
      <div className={cn(styles.block)}>
        <DetailsContent t0={"company details"}
          t1={`NZBN: ${data.nzbn ?? ''}`}
          t2={`Company No.: ${data.companyNumber ?? ''}`}
          t3={`Commenced : ${dayjs(data.commenced).format('YYYY-MM-DD') ?? ''}`}
        />

        <DetailsContent t0={"Contacts"}
          t1={data.address}
          t2={data.email}
          t3={data.phone}
        />
      </div>
      <div className={cn(styles.block, styles.list)}>
        <Space className={styles.flex} size={8} style={{}}>
          <AttachmentLinkLargeIcon />
          { data.lendingManagers?.map((x:any, index:number) => (
            <UserAvatar key={index} src={x.photo} size={24} alt={'user'} />
          )) }
        </Space>
        <DetailsItem text={maybePluralize(data.requestsCount ?? 0, "request")} Icon={RequestIcon} />
        <DetailsItem text={maybePluralize(data.projects?.length ?? 0, "project")} Icon={FolderBookmarkIcon} />
      </div>
    </Block>
  )
}

const DetailsContent = ({t0, t1, t2, t3}:{
  t0:string;
  t1:string;
  t2:string;
  t3:string;
}) => (
  <div className={styles.content}>
    <Text mb={16} uppercase size="2xs" weight="demiBold">{t0}</Text>
    <Text mb={8}>{t1}</Text>
    <Text mb={8}>{t2}</Text>
    <Text mb={8}>{t3}</Text>
  </div>
)
const DetailsItem = ({Icon, text}:{
  Icon: IconComponent
  text: string
}) => {
  return (
    <div className={styles.item}>
      <Space className={styles.flex} size={6}>
        <Icon className={styles['item-icon']} />
        <Text as="span" weight="demiBold" >
          {text}
        </Text>
      </Space>
      {/* <ArrowIcon className={styles['item-icon']} /> */}
    </div>
  )
}

const EditingAction = ({ initValues, isRequestLoading }: {
  isRequestLoading: boolean
  initValues: any
}) => {
  const permissions: PermissionType[] = useStore((state) => state.permissions)
  const [isOpen, handleOpen, handleClose] = useOpen()

  return (
    !permissions?.includes('edit org') ? null :
    <>
      <Button variant="secondary"
        disabled={isRequestLoading}
        onClick={handleOpen}
      >
        edit
      </Button>
      <Modal title={`Edit`} open={isOpen}
        onCancel={handleClose}
      >
        <OrgDetailsForm
          initValues={initValues}
          onClose={handleClose}
        />
      </Modal>
    </>
  )
}

const DisplayID = ({displayId}: {
  displayId: number
}) => (
  <Text uppercase spacing size="2xs" weight="demiBold" color="grey">
    ID {displayId}
  </Text>
)
